import { ERROR_CODE_MESSAGES } from '@shared/i18n/translations/errorCodes';

export const translations = {
  common: {
    loading: "Chargement...",
    error: "Erreur",
    success: "Succès",
  },
  errors: {
    unknown: "Une erreur inconnue s'est produite",
    taskActionUnavailable: "Cette action n'est pas disponible pour cette tâche.",
    codes: ERROR_CODE_MESSAGES,
    network: "Erreur de connexion réseau",
    validation: "Les données fournies sont invalides",
    authentication: "Authentification requise",
    authorization: "Vous n'avez pas les permissions nécessaires",
    notFound: "Ressource non trouvée",
    server: "Erreur du serveur",
    upstreamUnavailable: "Un service est temporairement indisponible. Veuillez réessayer dans quelques instants.",
    api: {
      general: "Erreur lors de la communication avec le serveur",
    },
  },
  /** The ported workspace. One block per screen, added as each screen is wired. */
  workspace: {
    storage: {
      subjectsRail: "Matières",
      title: "Mon stockage",
      addResource: "Ajouter une ressource",
      addSubject: "Créer une matière",
      subjectCreated: "Matière créée.",
      resourceCount_one: "{{count}} ressource",
      resourceCount_other: "{{count}} ressources",
      states: {
        sessionTitle: "Votre session a expiré",
        sessionBody: "Reconnectez-vous pour retrouver vos ressources.",
        sessionAction: "Se reconnecter",
        failureTitle: "Impossible de charger vos ressources",
        failureBody: "Le serveur n'a pas répondu comme prévu. {{detail}}",
        failureAction: "Réessayer",
        emptySubjectTitle: "Déposez votre premier fichier",
        emptySubjectDragging: "Relâchez pour l'ajouter",
        emptySubjectBody: "PDF, Word, PowerPoint ou image.",
        emptySubjectLabel: "Ajouter une ressource à {{subject}}. Ouvre la fenêtre d'ajout. Vous pouvez aussi déposer un fichier sur cette zone.",
        noSubjectTitle: "Aucune matière",
        noSubjectBody: "Créez une matière pour commencer à y ranger vos documents.",
      },
      document: {
        eyebrow: "Document",
        addedOn: "Ajouté le {{date}}",
        download: "Télécharger",
        downloading: "Téléchargement…",
        downloaded: "« {{name}} » a été téléchargé.",
        previewLoading: "Chargement de l'aperçu…",
        previewUnavailable: "Pas d'aperçu pour ce format.",
        previewFailed: "L'aperçu n'a pas pu être chargé.",
        rename: "Renommer",
        delete: "Supprimer définitivement",
        deleteMenu: "Supprimer…",
        more: "Plus d'actions",
        close: "Fermer",
        deleteSuccess: "Ressource supprimée.",
        removeFrom: "Retirer de {{className}}",
        category: "Reclasser",
        sharing: "Partage",
        sharingUnavailable: "Le partage n'est pas disponible pour cette ressource.",
        sharingFailed: "Le chargement des classes a échoué.",
        sharingEmpty: "Vous n'avez pas encore de classe.",
        history: "Historique de partage",
        notShared: "Non partagé",
        confirmTitle: "Supprimer définitivement ?",
        confirmSubtitle: "Cette action ne peut pas être annulée.",
        confirmBody: "« {{name}} » sera retiré de votre stockage.",
        cancel: "Annuler",
        renameTitle: "Renommer le document",
        renameSubtitle: "Le nom affiché dans votre stockage.",
        renameLabel: "Nom du document",
        renameRequired: "Le nom ne peut pas être vide.",
        renaming: "Renommage…",
        renameFailed: "Le renommage a échoué.",
        renameSuccess: "Document renommé.",
      },
      subject: {
        delete: "Supprimer",
        confirmTitle: "Supprimer cette matière ?",
        confirmSubtitle: "Cette action ne peut pas être annulée.",
        confirmBody: "« {{name}} » sera supprimée définitivement.",
        deleteSuccess: "Matière supprimée.",
        deleteFailed: "La suppression de la matière a échoué.",
        renameSuccess: "Matière renommée.",
      },
    },
    tasks: {
      states: {
        sessionTitle: "Votre session a expiré",
        sessionBody: "Reconnectez-vous pour retrouver vos tâches.",
        sessionAction: "Se reconnecter",
        failureTitle: "Impossible de charger vos tâches",
        failureBody: "Le serveur n'a pas répondu comme prévu. {{detail}}",
        failureAction: "Réessayer",
        emptyTitle: "Rien à faire",
        emptyBody: "Ajoutez une tâche ci-dessus.",
        filteredTitle: "Aucune tâche pour {{className}}",
        filteredBody:
          "Une tâche n'est pas encore rattachée à une classe : le filtre ne trouvera rien tant que l'API ne la transporte pas.",
      },
      composer: {
        classUnavailable:
          "La classe et la matière ne sont pas encore enregistrées avec la tâche.",
      },
      classRail: {
        unavailable: "Les tâches ne sont pas encore rattachées à une classe.",
      },
    },
  },

  classPage: {
    subject: {
      label: "Matière",
      only: "Matière : {{subject}}",
    },
    public: {
      topBar: {
        toLightTheme: "Passer en thème clair",
        toDarkTheme: "Passer en thème sombre",
        teacherSpace: "Espace enseignant",
      },
      gate: {
        title: "Cette classe est protégée",
        body: "Saisissez le code remis par {{teacher}} pour accéder aux ressources.",
        unnamedTeacher: "votre enseignant",
        teacherQuestion: "Vous êtes enseignant ?",
        signIn: "Se connecter",
      },
      code: {
        label: "Code d'accès",
        placeholder: "K7M4P2",
        submit: "Accéder à la classe",
        submitting: "Vérification…",
      },
      resources: {
        title: "Ressources",
        fileCount_one: "{{count}} fichier",
        fileCount_other: "{{count}} fichiers",
      },
      empty: {
        title: "Rien à consulter pour l'instant",
        body: "Votre enseignant n'a pas encore partagé de ressources avec cette classe. Revenez un peu plus tard.",
        bodyInSubject:
          "Votre enseignant n'a pas encore partagé de ressources en {{subject}}. Revenez un peu plus tard.",
      },
      deadLink: {
        title: "Ce lien ne mène nulle part",
        body: "Cette classe n'existe pas ou n'est plus partagée. Demandez un lien à jour à votre enseignant.",
      },
      failure: {
        title: "Chargement impossible",
        retry: "Réessayer",
      },
      reconnect: {
        title: "Bon retour",
        titleNamed: "Bon retour, {{firstName}}",
        body: "Vous consultez cette classe comme un élève. Reconnectez-vous pour retrouver votre espace enseignant.",
        continueAsStudent: "Continuer comme élève",
        signIn: "Me reconnecter",
      },
    },
  },

  navigation: {
  },
  dashboard: {
    header: {
      question: {
      },
      actions: {
      }
    },
    home: {
      emptyState: {
      }
    },
    subject: {
      resources: "ressources",
      createModal: {
        create: "Créer",
        errors: {
        }
      }
    },
    agenda: {
      today: "Aujourd'hui",
      tomorrow: "Demain",
      days: {
        monday: "Lundi",
      }
    },
    todo: {
      priority: {
      },
      createModal: {
        priority: {
        },
        create: "Créer",
        errors: {
        }
      }
    },
    shareResource: {
    },
    createClass: {
      next: "Continuer",
      submit: "Créer la classe"
    },
    sections: {
    },
    buttons: {
    },
    cards: {
      resources: "ressources",
      resource: "ressource",
      live: "Live",
    },
    storage: {
      emptyState: {
      },
      resources: "Ressources",
      deleteModal: {
        message: "Êtes-vous sûr de vouloir supprimer « {{name}} » ? Toutes les ressources qu'elle contient seront également supprimées. Cette action est irréversible.",
      },
      emptyCategory: {
      },
      addResourceModal: {
        submit: "Ajouter",
        errors: {
          generic: "Une erreur est survenue lors de l'envoi. Veuillez réessayer."
        }
      },
      resource: {
        deleteModal: {
          message: "Êtes-vous sûr de vouloir supprimer cette ressource ? Cette action est irréversible.",
        },
        errors: {
          generic: "Une erreur est survenue, veuillez réessayer",
          upstreamUnavailable: "Le service de stockage est temporairement indisponible. Veuillez réessayer dans quelques instants."
        }
      },
      errors: {
        delete: "Erreur lors de la suppression de la matière",
      },
      actions: {
        delete: "Supprimer la matière",
      },
      messages: {
        loading: "Chargement...",
      },
      renameModal: {
        errors: {
          required: "Le nom de la matière est obligatoire",
          maxLength: "Le nom ne doit pas dépasser 50 caractères",
          invalidChars: "Le nom contient des caractères non autorisés",
        }
      },
      createFolderModal: {
        create: "Créer",
      },
      createBoardModal: {
        create: "Créer le tableau",
      }
    },
    classroom: {
      addBoardModal: {
        existingBoards: {
        },
        createFromSubject: {
        },
        noSubjects: {
        },
        success: {
        },
        submit: "Ajouter",
      }
    },
    configuration: {
      categories: {
        create: "Créer",
        delete: "Supprimer",
        deleteModal: {
          message: "Êtes-vous sûr de vouloir supprimer cette catégorie ? Cette action est irréversible.",
        },
        errors: {
          create: "Erreur lors de la création de la catégorie",
          update: "Erreur lors de la modification de la catégorie",
          delete: "Erreur lors de la suppression de la catégorie"
        },
        loading: "Chargement des catégories...",
        emptyState: {
        }
      }
    }
  },
  boardSharing: {
    emptyState: {
    },
  },
};
